// <SetPassword>: the standalone `/reset-password` surface for an authenticated email-link login
// (product.md → Authentication → Endpoints). Reached when a teammate INVITE (set your initial
// password into an existing org) or a RECOVERY reset stashes a single-use password grant
// (AuthCallback → sessionStorage). A self-serve onboarder instead lands on the 2-step wizard, which
// folds the password step into onboarding: see CALLBACK_DESTINATION.setPassword.
//
// WHY it lives ABOVE the session switch (App.tsx), grant-gated rather than session-gated: the
// single-use grant: NOT the cookie session: is the real authorization for POST /auth/password,
// and the `/auth/me` session can lag a tick behind the callback's POST. Gating on the resolved
// session instead would bounce a freshly-recovered user to /login (session not yet resolved) or,
// once it resolves, to the dashboard (RedirectFromWizard): never letting them set the password.
// Gating on the grant renders this the instant the callback hands off, independent of `/auth/me`.
//
// Distinct from the onboarding wizard's step 1 (SetupLoginStep): that path goes on to create an
// ORG. An invite/recovery principal already has (or is joining) a group, so they set ONE password
// and land on the app: no org step. Both POST the grant to the same `/auth/password` route.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import { useNavigate } from '@client/shared/router/router.tsx';
import { HOME_PATH } from '@client/shared/router/validated-next.ts';
import {
  AuthGate,
  clearStashedGrant,
  invalidateSession,
  readStashedGrant,
} from '@client/shared/auth/index.ts';
import { PasswordSchema } from './inputs.ts';
import { postSetPassword } from './wizard-api.ts';

/** Just the new password: no name/org (this principal already has an account). Mirrors the
 *  server's MIN_PASSWORD_LENGTH floor via the shared {@link PasswordSchema}. */
const SetPasswordFormSchema = z.object({ password: PasswordSchema });
const EMPTY_SET_PASSWORD_FORM: z.infer<typeof SetPasswordFormSchema> = { password: '' };

export function SetPassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  // The grant is the entry ticket. If it's already gone (opened in another tab, spent, or
  // sessionStorage is unavailable) there is nothing to set here: hand off to the gate, which
  // routes them to the dashboard or the sign-in form as their session dictates.
  useEffect(() => {
    if (readStashedGrant() === null) navigate(HOME_PATH, { replace: true });
  }, [navigate]);

  const form = useForm({
    defaultValues: EMPTY_SET_PASSWORD_FORM,
    validators: { onSubmit: zodValidator(SetPasswordFormSchema) },
    onSubmit: async ({ value }) => {
      setFormError(null);

      const grant = readStashedGrant();
      if (!grant) {
        navigate(HOME_PATH, { replace: true });
        return;
      }

      const outcome = await postSetPassword({ grant, password: value.password });
      switch (outcome.kind) {
        case 'ok':
          // The single-use grant is spent: clear it so a refresh can't replay it.
          clearStashedGrant();
          // The password changed; re-resolve `/auth/me` so the gate lands on the dashboard with a
          // fresh session (and closes any mount-time race that left the cache unauthenticated).
          await invalidateSession(queryClient);
          navigate(HOME_PATH, { replace: true });
          return;
        case 'weak':
          setFormError('Choose a stronger password (at least 8 characters).');
          return;
        case 'invalid-grant':
          clearStashedGrant();
          setFormError('This password link has expired. Request a new one to continue.');
          return;
        case 'error':
          setFormError(outcome.message);
          return;
      }
    },
  });

  return (
    <AuthGate
      eyebrow="Account"
      heading="Set a new password"
      intro="Choose a new password for your Cheddule account."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        noValidate
      >
        <Stack gap={4}>
          <form.Field name="password">
            {(field) => (
              <TextField
                field={field}
                label="New password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
            )}
          </form.Field>

          {formError && (
            <Text size="sm" tone="danger" role="alert">
              {formError}
            </Text>
          )}

          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" size="lg" block loading={isSubmitting}>
                Set password
              </Button>
            )}
          </form.Subscribe>
        </Stack>
      </form>
    </AuthGate>
  );
}
